import * as React from 'react';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className="font-serif font-medium text-base text-ink mb-2">{title}</h3>
    {children}
  </div>
);

export const PrivacyContent: React.FC = () => (
  <>
    <p className="text-xs text-ink-500 mb-4">
      <strong className="text-ink">Effective Date:</strong> [PLACEHOLDER — insert date before launch]
      &nbsp;·&nbsp;
      <strong className="text-ink">Last Updated:</strong> [PLACEHOLDER]
    </p>
    <p className="text-xs font-medium uppercase tracking-widest text-oxblood mb-6">
      ⚠ Placeholder — Review with an attorney for CCPA compliance before public launch
    </p>

    <Section title="1. Who We Are">
      <p>
        Oracle Insight ("we," "us," "our") operates an AI-powered equity research platform at
        oracleinsight.ai. This Privacy Policy describes how we collect, use, and protect information
        about users of our Platform. By using the Platform, you agree to the collection and use of
        information as described herein.
      </p>
    </Section>

    <Section title="2. Information We Collect">
      <p className="mb-2"><strong className="text-ink">Information you provide directly:</strong></p>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>Account registration data: email address, display name, password (hashed by Firebase Authentication).</li>
        <li>Stock tickers, portfolio data, and other inputs you enter into the Platform.</li>
        <li>Communications you send to us.</li>
      </ul>
      <p className="mb-2"><strong className="text-ink">Information collected automatically:</strong></p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Usage data: features used, analyses run, pages visited, session duration.</li>
        <li>Device and browser information: browser type, operating system, screen resolution.</li>
        <li>Log data: IP address, timestamps, error logs.</li>
        <li>[PLACEHOLDER — If cookie consent banner is implemented, disclose cookie types here.]</li>
      </ul>
    </Section>

    <Section title="3. How We Use Your Information">
      <ul className="list-disc pl-5 space-y-1">
        <li>To provide, operate, and improve the Platform and its features.</li>
        <li>To authenticate your identity and manage your account.</li>
        <li>To process subscription payments and manage billing.</li>
        <li>To send transactional emails (account confirmation, password reset, billing receipts).</li>
        <li>To monitor Platform performance, diagnose errors, and prevent abuse.</li>
        <li>To analyze aggregate usage patterns and improve our AI models and product.</li>
        <li>To comply with legal obligations.</li>
      </ul>
    </Section>

    <Section title="4. Third-Party Services We Use">
      <p className="mb-2">
        The Platform relies on the following third-party services, each of which may process your
        data under their own privacy policies:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong className="text-ink">Firebase (Google):</strong> Authentication and Firestore database. Your email, account status, and analysis data are stored in Firebase. Subject to Google's Privacy Policy.</li>
        <li><strong className="text-ink">Google Gemini API:</strong> AI analysis generation. Stock tickers and financial data are sent to Google's Gemini API to produce research outputs. No personally identifiable information is sent. Subject to Google's API Terms.</li>
        <li><strong className="text-ink">Financial Modeling Prep (FMP):</strong> Financial data provider. Ticker symbols are sent to FMP to retrieve financial data. Subject to FMP's Terms.</li>
        <li><strong className="text-ink">Stripe:</strong> Payment processing. Stripe handles all payment card data. Oracle Insight does not store payment card numbers. Subject to Stripe's Privacy Policy.</li>
        <li><strong className="text-ink">PostHog:</strong> Product analytics. Anonymized usage events (analysis runs, feature interactions) are sent to PostHog. Subject to PostHog's Privacy Policy.</li>
        <li><strong className="text-ink">Sentry:</strong> Error monitoring. Application error data including stack traces may be captured. Subject to Sentry's Privacy Policy.</li>
        <li><strong className="text-ink">Resend:</strong> Transactional email delivery. Your email address is used to deliver account and billing emails. Subject to Resend's Privacy Policy.</li>
      </ul>
    </Section>

    <Section title="5. Data Sharing">
      <p>
        We do not sell your personal information. We do not share your personal information with
        third parties except: (a) with the service providers listed in Section 4 as necessary to
        operate the Platform; (b) to comply with applicable law, legal process, or government
        requests; (c) to protect the rights, property, or safety of Oracle Insight or others;
        (d) in connection with a merger, acquisition, or sale of all or a portion of our assets,
        in which case we will notify you.
      </p>
    </Section>

    <Section title="6. Data Retention">
      <p>
        We retain your account information and analysis history for as long as your account is
        active or as needed to provide the Platform. If you request deletion of your account, we
        will delete or anonymize your personal data within a reasonable time, subject to our legal
        obligations to retain certain records. [PLACEHOLDER — Define specific retention periods
        before launch.]
      </p>
    </Section>

    <Section title="7. Security">
      <p>
        We implement commercially reasonable administrative, technical, and physical safeguards to
        protect your information. Authentication is handled by Firebase, a Google enterprise
        service. API keys are stored in Google Cloud Secret Manager and are never exposed to the
        browser. However, no method of transmission over the internet or electronic storage is 100%
        secure. We cannot guarantee absolute security.
      </p>
    </Section>

    <Section title="8. Your Rights (California Residents — CCPA)">
      <p className="mb-2">
        If you are a California resident, you have the following rights under the California
        Consumer Privacy Act (CCPA):
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong className="text-ink">Right to Know:</strong> You may request disclosure of the categories and specific pieces of personal information we have collected about you.</li>
        <li><strong className="text-ink">Right to Delete:</strong> You may request deletion of your personal information, subject to certain exceptions.</li>
        <li><strong className="text-ink">Right to Opt-Out:</strong> We do not sell personal information. If this changes, we will provide a "Do Not Sell" mechanism.</li>
        <li><strong className="text-ink">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
      </ul>
      <p className="mt-2">
        To exercise your rights, contact us at [PLACEHOLDER — legal@oracleinsight.ai]. We will
        respond within 45 days as required by law.
      </p>
    </Section>

    <Section title="9. Cookies and Analytics">
      <p>
        The Platform uses functional cookies necessary for authentication and session management
        (provided by Firebase). We use PostHog for product analytics, which may set analytics
        cookies or use local storage. [PLACEHOLDER — If you implement a cookie consent banner,
        update this section to describe opt-in/opt-out mechanism and cookie categories.]
      </p>
    </Section>

    <Section title="10. Children's Privacy">
      <p>
        The Platform is not directed to children under the age of 13. We do not knowingly collect
        personal information from children under 13. If you believe a child has provided us with
        personal information, please contact us immediately and we will take steps to delete it.
      </p>
    </Section>

    <Section title="11. Changes to This Policy">
      <p>
        We may update this Privacy Policy from time to time. We will notify registered users of
        material changes by email or by posting a notice on the Platform. The "Last Updated" date
        at the top of this page indicates when the policy was last revised. Your continued use of
        the Platform after changes are posted constitutes your acceptance of the updated policy.
      </p>
    </Section>

    <Section title="12. Contact">
      <p>
        Questions, requests, or concerns regarding this Privacy Policy may be directed to:
        [PLACEHOLDER — legal@oracleinsight.ai or your designated privacy contact].
      </p>
    </Section>
  </>
);
